// Unified message engine for the Smart Communication Assistant.
// Single source of truth for situations + how each renders for WhatsApp / Email.
// All messages use a FORMAL B2B tone (per RENVIRO communication standard).

const COMPANY_NAME = "RENVIRO Systems";

const greetingNow = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const firstName = (name) => {
  if (!name) return "there";
  const parts = String(name).trim().split(/\s+/);
  return parts[0] || "there";
};

const sig = (sender) => {
  const name = sender?.name || "Sales Team";
  return `Best regards,\n${name}\n${COMPANY_NAME}`;
};

// ---------- SITUATIONS ----------
// Each situation defines: label, hint, subject (for email), body builder.
// Body is the *core* message — channel-specific wrappers add it at the right place.

export const SITUATIONS = {
  intro: {
    label: "Introduction",
    hint: "First contact with a new prospect",
    subject: ({ company }) => `Introduction — ${COMPANY_NAME}${company ? ` × ${company}` : ""}`,
    body: ({ contact, sender }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. I hope this message finds you well.

I wanted to introduce myself and explore how our reliability and ultrasound diagnostic solutions could add value to your operations. Would you be open to a brief conversation at your convenience?

${sig(sender)}`,
  },

  follow_up_deal: {
    label: "Follow-up on deal",
    hint: "General check-in on an in-progress deal",
    subject: ({ deal }) => `Follow-up${deal?.title ? ` — ${deal.title}` : ""}`,
    body: ({ contact, sender, deal }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME} writing in reference to ${deal?.title ? `\u201C${deal.title}\u201D` : "our recent discussion"}.

I wanted to follow up on the next steps we had outlined. Could you please share an update at your earliest convenience, or let me know a suitable time for a brief call?

${sig(sender)}`,
  },

  follow_up_action: {
    label: "Follow-up — specific action",
    hint: "Chase a specific pending action item",
    subject: ({ deal, action }) => `Follow-up: ${action || (deal?.title || "next steps")}`,
    body: ({ contact, sender, deal, action }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. As a follow-up on ${deal?.title ? `\u201C${deal.title}\u201D` : "our discussion"}:

${action ? `\u2022 ${action}` : "\u2022 Awaiting your inputs to move ahead."}

Kindly let me know your thoughts at your convenience.

${sig(sender)}`,
  },

  meeting_confirm: {
    label: "Confirm meeting",
    hint: "Reconfirm a scheduled meeting",
    subject: ({ deal, meetingWhen }) => `Meeting confirmation${meetingWhen ? ` — ${meetingWhen}` : ""}${deal?.title ? ` (${deal.title})` : ""}`,
    body: ({ contact, sender, deal, meetingWhen, meetingLocation }) =>
`${greetingNow()} ${firstName(contact?.name)},

Confirming our scheduled meeting${meetingWhen ? ` on ${meetingWhen}` : ""}${deal?.title ? ` to discuss \u201C${deal.title}\u201D` : ""}.${meetingLocation ? `\nVenue: ${meetingLocation}` : ""}

Please let me know if a different time would be more convenient. Looking forward to our conversation.

${sig(sender)}`,
  },

  quotation_share: {
    label: "Share quotation",
    hint: "Send across a fresh quotation",
    subject: ({ deal }) => `Quotation${deal?.title ? ` — ${deal.title}` : ""}`,
    body: ({ contact, sender, deal }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. As discussed, please find attached our quotation${deal?.title ? ` for \u201C${deal.title}\u201D` : ""}. I will share the document over email shortly.

Should you have any questions or require clarifications, I would be happy to schedule a call.

${sig(sender)}`,
  },

  quotation_reminder: {
    label: "Quotation reminder",
    hint: "Polite nudge after a quotation has been shared",
    subject: ({ deal }) => `Following up on our quotation${deal?.title ? ` — ${deal.title}` : ""}`,
    body: ({ contact, sender, deal }) =>
`${greetingNow()} ${firstName(contact?.name)},

I hope you are doing well. I wanted to follow up on the quotation we shared${deal?.title ? ` for \u201C${deal.title}\u201D` : ""}.

Could you please share your feedback, or let me know if any clarifications, revisions, or commercial discussions would help move things forward?

${sig(sender)}`,
  },

  thank_you_meeting: {
    label: "Thank you after meeting",
    hint: "Recap and next steps after a meeting",
    subject: ({ deal }) => `Thank you${deal?.title ? ` — ${deal.title}` : ""}`,
    body: ({ contact, sender, deal }) =>
`${greetingNow()} ${firstName(contact?.name)},

Thank you for your time today${deal?.title ? ` discussing \u201C${deal.title}\u201D` : ""}. It was a pleasure speaking with you.

I will share a written recap along with the next steps shortly. In the meantime, please feel free to reach out for any clarifications.

${sig(sender)}`,
  },

  re_engagement: {
    label: "Re-engagement",
    hint: "Reach out to a dormant account",
    subject: () => `Reconnecting from ${COMPANY_NAME}`,
    body: ({ contact, sender, deal }) =>
`${greetingNow()} ${firstName(contact?.name)},

It has been a while since we last connected${deal?.title ? ` regarding \u201C${deal.title}\u201D` : ""}. I wanted to reach out and check in.

If your priorities have evolved, I would be happy to align on what is most useful at this stage and arrange a brief discussion accordingly.

${sig(sender)}`,
  },

  // ---------- MARKETING OUTREACH (raw leads collected from events) ----------

  event_intro: {
    label: "Post-event introduction",
    hint: "First message after meeting at a seminar/exhibition/conference",
    subject: ({ event }) => `Great connecting at ${event || "our recent event"} — ${COMPANY_NAME}`,
    body: ({ contact, sender, event }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. It was a pleasure connecting with you at ${event || "our recent event"}.

As discussed, we specialise in industrial reliability, vibration diagnostics and ultrasound-based condition monitoring. I would welcome the opportunity to share how we have helped similar plants reduce unplanned downtime.

Would a brief 15-minute call this week work for you?

${sig(sender)}`,
  },

  reference_intro: {
    label: "Reference introduction",
    hint: "Outreach via a mutual contact / referral",
    subject: ({ company }) => `Referred introduction — ${COMPANY_NAME}${company ? ` × ${company}` : ""}`,
    body: ({ contact, sender }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. I was referred to you in connection with the reliability and predictive-maintenance challenges your team is currently navigating.

We have supported plants across cement, steel, power and process industries with ultrasound diagnostics and on-site vibration analysis. I would be glad to share a couple of relevant case studies and explore if a short discussion would be useful.

Could you please share a convenient time for a brief call this week?

${sig(sender)}`,
  },

  cold_outreach: {
    label: "Cold outreach (campaign / list)",
    hint: "First message to a marketing-list contact (no prior interaction)",
    subject: () => `Reducing unplanned downtime — ${COMPANY_NAME}`,
    body: ({ contact, sender, company }) =>
`${greetingNow()} ${firstName(contact?.name)},

This is ${sender?.name || "our team"} from ${COMPANY_NAME}. We work with industrial plants on reliability, ultrasound diagnostics and condition monitoring${company ? ` and have been following ${company}'s work with interest` : ""}.

If unplanned downtime, bearing failures, or steam-trap losses are something your team currently tracks, I would love to share a short note on how we have helped similar facilities recover lost uptime.

Could we schedule a brief 10-minute call at your convenience?

${sig(sender)}`,
  },

  // ---------- QUOTATION SHARING ----------

  quotation_send: {
    label: "Quotation — share PDF",
    hint: "Formal quotation cover note (with PDF link / attachment)",
    subject: ({ deal, quotation, company }) =>
      `Quotation ${quotation?.number || ""} — ${company?.name || COMPANY_NAME}${deal?.title ? ` (${deal.title})` : ""}`,
    body: ({ contact, sender, deal, quotation, totalText, validUntil, pdfLink, company }) =>
`${greetingNow()} ${firstName(contact?.name)},

Please find attached our formal quotation${quotation?.number ? ` <b>${quotation.number}</b>` : ""}${deal?.title ? ` for ${deal.title}` : ""}.

Summary
• Total value: ${totalText || "as detailed in the attached PDF"}
${validUntil ? `• Valid until: ${validUntil}\n` : ""}${quotation?.subject ? `• Scope: ${quotation.subject}\n` : ""}
The quotation includes itemised pricing, applicable GST, terms of payment, and our bank details for your records.${pdfLink ? `\n\nPDF: ${pdfLink}` : ""}

We would be happy to walk you through the quotation over a 15-minute call and address any questions on scope, timelines or commercials.

Looking forward to your feedback.

${sig(sender || (company ? { name: company.signatory_name, designation: company.signatory_designation, email: company.email, phone: company.phone, company: company.name } : undefined))}`,
  },

  quotation_followup: {
    label: "Quotation — gentle reminder",
    hint: "Polite nudge a few days after sending a quotation",
    subject: ({ quotation }) =>
      `Following up on quotation ${quotation?.number || ""}`,
    body: ({ contact, sender, quotation, validUntil }) =>
`${greetingNow()} ${firstName(contact?.name)},

I hope this message finds you well. I wanted to follow up on our quotation${quotation?.number ? ` <b>${quotation.number}</b>` : ""}${quotation?.subject ? ` for ${quotation.subject}` : ""}.

If you have had a chance to review it, I would welcome any feedback or clarifications you may need on scope, commercials, or timelines. ${validUntil ? `As a quick note, the quotation is valid until ${validUntil}.` : ""}

Happy to schedule a brief call at your convenience to walk through it.

${sig(sender)}`,
  },
};

export const SITUATION_ORDER = [
  "quotation_send",
  "quotation_followup",
  "event_intro",
  "reference_intro",
  "cold_outreach",
  "follow_up_action",
  "follow_up_deal",
  "quotation_reminder",
  "quotation_share",
  "meeting_confirm",
  "thank_you_meeting",
  "intro",
  "re_engagement",
];

// ---------- BUILD ----------

export function buildMessage({ situation, contact, sender, deal, action, meetingWhen, meetingLocation, event, quotation, totalText, validUntil, pdfLink, company }) {
  const def = SITUATIONS[situation] || SITUATIONS.follow_up_deal;
  const ctx = { contact, sender, deal, action, meetingWhen, meetingLocation, event, quotation, totalText, validUntil, pdfLink, company, company_name: company?.name || contact?.company || deal?.company };
  return {
    subject: def.subject(ctx),
    body: def.body(ctx),
  };
}

// ---------- WHATSAPP HELPERS ----------

export function normalizePhoneForWa(phone) {
  if (!phone) return "";
  const trimmed = String(phone).trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (!hasPlus && digits.length === 10 && /^[6-9]/.test(digits)) {
    return "91" + digits;
  }
  return digits;
}

export function buildWaUrl(phone, body) {
  const num = normalizePhoneForWa(phone);
  if (!num) return null;
  const text = body ? `?text=${encodeURIComponent(body)}` : "";
  return `https://wa.me/${num}${text}`;
}

export function buildMailto(email, subject, body) {
  if (!email) return null;
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${email}${params.length ? "?" + params.join("&") : ""}`;
}

export const CHANNELS = {
  whatsapp: { label: "WhatsApp", needs: "phone" },
  email: { label: "Email", needs: "email" },
  copy: { label: "Copy", needs: null },
};
